import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { formatSlotHours } from "@/shared/lib/private-lesson-slot-duration";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { isBefore } from "date-fns";
import { ArrowLeftIcon, PackageIcon, UserIcon } from "lucide-react";
import { useState } from "react";
import {
  useBulkSchedulePrivateLessons,
  usePrivateLessonPackageQuery,
} from "../api/queries";
import { BulkScheduleForm } from "../ui/BulkScheduleForm";

export function BulkSchedulePrivateLessonsPage() {
  const { packageId } = useParams({
    from: "/app/private-lessons/packages/$packageId/schedule",
  });
  const nav = useNavigate();
  const bulkSchedule = useBulkSchedulePrivateLessons();
  const [totalHoursToSchedule, setTotalHoursToSchedule] = useState(0);

  const { data: packageData, isLoading } =
    usePrivateLessonPackageQuery(packageId);

  const handleSubmit = async (
    values: Parameters<typeof bulkSchedule.mutateAsync>[0],
  ) => {
    try {
      const result = await bulkSchedule.mutateAsync(values);
      // Navigate to success or booking list
      nav({
        to: "/app/private-lessons/packages/$packageId",
        params: { packageId },
      });
    } catch {
      // Error handling is done by the mutation
    }
  };

  const bulkScheduleBreadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
        { label: "ແພັກເກດຮຽນສ່ວນຕົວ", to: "/app/private-lessons/packages" },
        { label: "ກຳນົດຕາຕະລາງ" },
      ]}
    />
  );

  if (isLoading) {
    return (
      <>
        <Header>{bulkScheduleBreadcrumb}</Header>
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (!packageData) {
    return (
      <>
        <Header>{bulkScheduleBreadcrumb}</Header>
        <Main>
          <div className="flex flex-col items-center justify-center py-12">
            <p className="mb-4 text-muted-foreground">ບໍ່ພົບ package</p>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/private-lessons/templates" })}
            >
              <ArrowLeftIcon className="mr-2 size-4" />
              ກັບໄປ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  // Validate package status
  if (packageData.status !== "active") {
    return (
      <>
        <Header>{bulkScheduleBreadcrumb}</Header>
        <Main>
          <div className="flex flex-col items-center justify-center py-12">
            <p className="mb-4 text-muted-foreground">
              Package ບໍ່ສາມາດໃຊ້ງານໄດ້ (Status: {packageData.status})
            </p>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/private-lessons/templates" })}
            >
              <ArrowLeftIcon className="mr-2 size-4" />
              ກັບໄປ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  // Check if package is expired
  if (isBefore(packageData.expireDate, new Date())) {
    return (
      <>
        <Header>{bulkScheduleBreadcrumb}</Header>
        <Main>
          <div className="flex flex-col items-center justify-center py-12">
            <p className="mb-4 text-muted-foreground">
              Package ໝົດອາຍຸແລ້ວ (Expire Date:{" "}
              {formatDate(packageData.expireDate)})
            </p>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/private-lessons/templates" })}
            >
              <ArrowLeftIcon className="mr-2 size-4" />
              ກັບໄປ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>{bulkScheduleBreadcrumb}</Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ກຳນົດຕາຕະລາງຮຽນສ່ວນຕົວຫຼາຍຄັ້ງ
            </h2>
            <p className="text-muted-foreground">
              ກຳນົດຕາຕະລາງຮຽນຫຼາຍຄັ້ງສຳລັບ package ທີ່ເລືອກແລ້ວ
            </p>
          </div>
        </div>

        {/* Student and Package Overview */}
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <UserIcon className="size-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="font-medium text-muted-foreground text-xs uppercase">
                  ນັກຮຽນ
                </p>
                <p className="font-semibold">{packageData.studentName}</p>
                <p className="text-muted-foreground text-sm">
                  ເລກສະມາຊິກ: {packageData.studentMemberId}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <PackageIcon className="size-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="font-medium text-muted-foreground text-xs uppercase">
                  ແພັກເກດ
                </p>
                <p className="font-semibold">{packageData.templateName}</p>
                <p className="text-muted-foreground text-sm">
                  {packageData.totalHours} ຊົ່ວໂມງທັງໝົດ
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Hour Summary */}
        <div className="mt-4 rounded-lg border bg-muted/50 p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">ທີເຫຼືອ:</span>
              <p className="font-bold">{packageData.remainingHours} ຊົ່ວໂມງ</p>
            </div>
            <div>
              <span className="text-muted-foreground">ຈະກຳນົດ:</span>
              <p className="font-bold" id="to-schedule">
                {formatSlotHours(totalHoursToSchedule)} ຊົ່ວໂມງ
              </p>
            </div>
          </div>
        </div>

        {/* Bulk Schedule Form */}
        <div className="mt-6">
          <BulkScheduleForm
            packageId={packageId}
            remainingHours={packageData.remainingHours}
            packageLocationKind={packageData.templateDefaultLocationKind}
            onTotalHoursChange={setTotalHoursToSchedule}
            onSubmit={handleSubmit}
            submitting={bulkSchedule.isPending}
          />
        </div>
      </Main>
    </>
  );
}
